import {
  getBlockSnapshotContentBound,
  offsetBlockSnapshots,
} from '@blocksuite/affine/gfx/template';
import type {
  BlockSnapshot,
  SliceSnapshot,
  Store,
} from '@blocksuite/affine/store';

const MARKETPLACE_APPEND_GAP = 80;

export function placeMarketplaceTemplateSnapshotForAppend(
  snapshot: SliceSnapshot,
  targetDoc: Store,
  options: { gap?: number } = {}
): SliceSnapshot {
  removeEmptyDefaultNotes(snapshot);

  const templateBound = getMarketplaceTemplateSnapshotBound(snapshot);
  const targetBound = getMarketplaceStoreContentBound(targetDoc);

  if (!templateBound || !targetBound) return snapshot;

  offsetBlockSnapshots(
    snapshot.content,
    targetBound.x +
      targetBound.w +
      (options.gap ?? MARKETPLACE_APPEND_GAP) -
      templateBound.x,
    targetBound.y - templateBound.y
  );

  return snapshot;
}

export function removeMergedMarketplaceDocLinks(
  snapshot: SliceSnapshot,
  mergedDocIds: ReadonlySet<string>
): void {
  snapshot.content = filterMergedDocLinks(snapshot.content, mergedDocIds);
}

export function getMarketplaceTemplateSnapshotBound(snapshot: SliceSnapshot) {
  return getBlockSnapshotContentBound(snapshot.content);
}

export function getMarketplaceStoreContentBound(store: Store) {
  return getBlockSnapshotContentBound(store.root?.children ?? []);
}

export function removeEmptyDefaultNotes(snapshot: SliceSnapshot): void {
  snapshot.content = snapshot.content.filter(block => !isEmptyNote(block));
}

function isEmptyNote(block: BlockSnapshot): boolean {
  if (block.flavour !== 'affine:note') return false;
  if (block.children.length === 0) return true;
  if (block.children.length !== 1) return false;

  const onlyChild = block.children[0];
  return onlyChild ? isEmptyParagraph(onlyChild) : true;
}

function isEmptyParagraph(block: BlockSnapshot): boolean {
  if (block.flavour !== 'affine:paragraph') return false;
  if (block.children.length > 0) return false;

  const text = block.props.text;
  if (!isRecord(text)) return true;
  const delta = text.delta;

  return (
    !Array.isArray(delta) ||
    delta.every(
      item => !isRecord(item) || String(item.insert ?? '').length === 0
    )
  );
}

function filterMergedDocLinks(
  blocks: readonly BlockSnapshot[],
  mergedDocIds: ReadonlySet<string>
): BlockSnapshot[] {
  return blocks
    .filter(block => !isMergedDocLink(block, mergedDocIds))
    .map(block => {
      block.children = filterMergedDocLinks(block.children, mergedDocIds);
      return block;
    });
}

function isMergedDocLink(
  block: BlockSnapshot,
  mergedDocIds: ReadonlySet<string>
): boolean {
  if (
    block.flavour !== 'affine:embed-linked-doc' &&
    block.flavour !== 'affine:embed-synced-doc'
  ) {
    return false;
  }
  const pageId = block.props.pageId;
  return typeof pageId === 'string' && mergedDocIds.has(pageId);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
